import type { CSSProperties, ComponentPropsWithoutRef } from "react";
import { forwardRef, useEffect, useRef } from "react";
import mergeRefs from "merge-refs";
import type { RefCallBack } from "react-hook-form";
import { Controller, useFormContext } from "react-hook-form";

export interface AutoExpandingTextInputProps
  extends ComponentPropsWithoutRef<"textarea"> {
  name: string;
  style?: CSSProperties;
}

export const AutoExpandingTextInput = forwardRef<
  HTMLTextAreaElement,
  AutoExpandingTextInputProps
>((props, ref) => {
  const { name, style = {}, ...passThroughProps } = props;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { watch, control } = useFormContext();

  const currentValue = watch(name) as string;

  useEffect(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = "inherit";
    textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
  }, [currentValue]);

  return (
    <div className="react-auto-expanding-text-input form-input">
      <Controller
        name={name}
        control={control}
        render={({
          field: { onChange, value, ref: fieldRef },
        }: {
          field: {
            onChange: (value: string) => void;
            value: string;
            ref: RefCallBack;
          };
        }) => (
          <textarea
            {...passThroughProps}
            value={value}
            onChange={event => {
              event.preventDefault();
              onChange(event.target.value.replaceAll("\n", ""));
            }}
            ref={mergeRefs<HTMLTextAreaElement>(textareaRef, ref, fieldRef)}
            className="form-control placeholder:text-light placeholder:opacity-100"
            rows={1}
            style={{
              ...style,
              resize: "none",
            }}
          />
        )}
      />
    </div>
  );
});

AutoExpandingTextInput.displayName = "AutoExpandingTextInput";
